/**
 * Utility functions for handling beasiswa data from markdown files
 */
import { parseFrontmatter } from './markdown';

// Default placeholder image from an external source
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80';

// Map of beasiswa images to external URLs
const BEASISWA_IMAGES = {
  'fulbright': 'https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  'lpdp': 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  'chevening': 'https://images.unsplash.com/photo-1526378722484-bd91ca387e72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1469&q=80',
  'erasmus': 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  'daad': 'https://images.unsplash.com/photo-1605722243979-fe0be8cbb4b9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
};

// Function to get external image URL based on local path or name
const getExternalImageUrl = (imagePath) => {
  if (!imagePath) return DEFAULT_IMAGE;
  
  // If it's already an external URL, return it as is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // Extract the image name from the path
  const imageName = imagePath.split('/').pop().split('.')[0].toLowerCase();
  
  // Check if we have a mapping for this image
  for (const [key, url] of Object.entries(BEASISWA_IMAGES)) {
    if (imageName.includes(key)) {
      return url;
    }
  }
  
  // Return default image if no mapping found
  return DEFAULT_IMAGE;
};

// Function to load all beasiswa posts
export const loadBeasiswaPosts = async () => {
  try {
    // Get the list of beasiswa posts from the index.json file
    const response = await fetch('/data/beasiswa/index.json');
    if (!response.ok) {
      throw new Error('Failed to fetch beasiswa index');
    }
    
    const beasiswaIndex = await response.json();
    
    // Replace local image paths with external URLs
    const beasiswaWithExternalImages = beasiswaIndex.map(beasiswa => ({
      ...beasiswa,
      coverImage: getExternalImageUrl(beasiswa.coverImage)
    }));
    
    // Return the beasiswa list from the index
    return beasiswaWithExternalImages;
  } catch (error) {
    console.error('Error loading beasiswa posts:', error);
    return [];
  }
};

// Function to get a beasiswa by slug
export const getBeasiswaBySlug = async (slug) => {
  try {
    const contentResponse = await fetch(`/data/beasiswa/${slug}.md`);
    
    if (!contentResponse.ok) {
      throw new Error(`Failed to fetch beasiswa with slug ${slug}`);
    }
    
    const markdownText = await contentResponse.text();
    const { frontmatter, content } = parseFrontmatter(markdownText);
    
    return {
      slug,
      title: frontmatter.title || 'Untitled Beasiswa',
      date: frontmatter.date || new Date().toISOString().split('T')[0],
      deadline: frontmatter.deadline || new Date().toISOString().split('T')[0],
      author: frontmatter.author || 'Admin',
      excerpt: frontmatter.excerpt || 'No excerpt available',
      content: content,
      coverImage: getExternalImageUrl(frontmatter.coverImage),
      country: frontmatter.country || 'Unknown',
      university: frontmatter.university || 'Unknown',
      degree: frontmatter.degree || 'All Levels',
      website: frontmatter.website || '',
      tags: frontmatter.tags || [],
    };
  } catch (error) {
    console.error(`Error loading beasiswa with slug ${slug}:`, error);
    return null;
  }
};